import type { Component } from 'vue';
import type { RouteRecordRaw } from 'vue-router';

import { Layout } from '@/core/layout/layout';

export enum DevRoute {
    Root = 'dev',
}

/** Menu order and titles. A folder under `pages/` that isn't listed here is not served. */
export const devSections = [
    { id: 'atoms', title: 'Atoms' },
    { id: 'molecules', title: 'Molecules' },
    { id: 'organisms', title: 'Organisms' },
    { id: 'controls', title: 'Business controls' },
    { id: 'composables', title: 'Composables' },
    { id: 'forms', title: 'Forms' },
    { id: 'i18n', title: 'i18n' },
] as const;

export type DevSectionId = (typeof devSections)[number]['id'];

/**
 * Every `pages/<section>/*.vue` file becomes a sandbox page at `/_dev/<section>/<kebab-case-file-name>`,
 * so adding a demo is just dropping a file into a section folder (`pages/atoms/`, `pages/forms/`…).
 * A component family with several pages goes in a subfolder named after its `src/shared/ui` folder
 * (`pages/atoms/Typography/Text.vue`): the menu groups it, and the URL stays `/_dev/atoms/text`.
 */
const pages = import.meta.env.DEV ? import.meta.glob<{ default: Component }>(['./pages/*/*.vue', './pages/*/*/*.vue']) : {};

export const toDevPage = (path: string): { section: string; family?: string; slug: string } => {
    const [, section = '', family, name = ''] = path.match(/pages\/([^/]+)\/(?:([^/]+)\/)?([^/]+)\.vue$/) ?? [];

    return {
        section,
        ...(family ? { family } : {}),
        slug: name.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase(),
    };
};

const isDevSection = (id: string): id is DevSectionId => devSections.some((section) => section.id === id);

export const devPages = Object.entries(pages)
    .map(([path, component]) => ({ ...toDevPage(path), component }))
    .filter((page): page is typeof page & { section: DevSectionId } => isDevSection(page.section))
    .sort((a, b) => a.slug.localeCompare(b.slug));

export const toDevRouteName = (page: { section: string; slug: string }): string => `${DevRoute.Root}-${page.section}-${page.slug}`;

/**
 * Empty outside the dev server. Both DEV guards in this file are required: Rollup emits
 * chunks for `import()` calls in kept code even when the router never registers them,
 * so the glob and the route records must be dead branches in production builds.
 */
export const routes: RouteRecordRaw[] = import.meta.env.DEV
    ? [
          {
              path: '/_dev',
              meta: {
                  layout: Layout.Blank,
              },
              component: () => import('./DevRoot.vue'),
              children: [
                  {
                      path: '',
                      name: DevRoute.Root,
                      component: () => import('./views/DevIndex.vue'),
                  },
                  ...devPages.map((page) => ({
                      path: `${page.section}/${page.slug}`,
                      name: toDevRouteName(page),
                      component: page.component,
                  })),
              ],
          },
      ]
    : [];
